import { useState, useEffect } from 'react';

export default function TransformationSection() {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    if (document.fonts) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    } else {
      setFontsLoaded(true);
    }
  }, []);

  const fontFamily = fontsLoaded ? 'Montserrat, sans-serif' : '-apple-system, BlinkMacSystemFont, sans-serif';
  const fontFamilyInter = fontsLoaded ? 'Inter, sans-serif' : '-apple-system, BlinkMacSystemFont, sans-serif';

  return (
    <section className="relative py-16 sm:py-32 bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
      {/* Partículas de fundo - apenas desktop */}
      <div className="hidden sm:block absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-yellow-400 rounded-full opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${4 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Cabeçalho da seção */}
        <div className="text-center mb-12 sm:mb-20">
          <span className="text-yellow-400 text-xs sm:text-sm font-bold tracking-widest uppercase mb-4 block" style={{ fontFamily }}>
            A TRANSFORMAÇÃO
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6" style={{ fontFamily }}>
            Da Mente <span className="text-gray-500">Confusa</span> à Mente <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">Dourada</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed px-4" style={{ fontFamily: fontFamilyInter }}>
            Nada muda do lado de fora até você mudar por dentro
          </p>
        </div>

        {/* Grid Antes e Depois */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {/* ANTES */}
          <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-700 hover:border-gray-600 transition-all duration-500">
            <div className="absolute -top-4 sm:-top-5 left-6 sm:left-8">
              <div className="bg-gray-700 text-gray-300 px-4 sm:px-6 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                <span className="font-bold text-base sm:text-lg" style={{ fontFamily }}>
                  ANTES
                </span>
              </div>
            </div>
            
            <div className="mt-6 sm:mt-8 mb-4 sm:mb-6">
              <img 
                src="https://readdy.ai/api/search-image?query=dark%20confused%20brain%20with%20gray%20neural%20networks%20and%20mental%20fog%2C%20confusion%20and%20chaos%20concept%2C%20dim%20lighting%20with%20scattered%20thoughts%20and%20blocked%20pathways%2C%20premium%20dark%20atmosphere&width=350&height=250&seq=confused-mobile&orientation=landscape"
                alt="Mente confusa"
                className="w-full rounded-2xl shadow-xl mb-4 sm:mb-6 sm:hidden"
                loading="lazy"
                decoding="async"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=dark%20confused%20brain%20with%20gray%20neural%20networks%20and%20mental%20fog%2C%20confusion%20and%20chaos%20concept%2C%20dim%20lighting%20with%20scattered%20thoughts%20and%20blocked%20pathways%2C%20premium%20dark%20atmosphere&width=500&height=350&seq=confused-premium&orientation=landscape"
                alt="Mente confusa"
                className="w-full rounded-2xl shadow-xl mb-4 sm:mb-6 hidden sm:block"
                loading="lazy"
                decoding="async"
              />
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-400 mb-4 sm:mb-6" style={{ fontFamily }}>
              A Mente Confusa
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-red-400 text-xl sm:text-2xl mt-1 flex-shrink-0"></i>
                <p className="text-gray-400 text-sm sm:text-base">Desordem. Cansaço. Ruído mental constante.</p>
              </div>
              
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed pl-8 sm:pl-9">
                A mente comum vive em modo sobrevivência. Milhares de pensamentos soltos, energias dispersas e falta de direção clara.
              </p>
              
              <div className="bg-gray-800/50 border border-gray-700 p-3 sm:p-4 rounded-xl pl-8 sm:pl-9">
                <p className="text-gray-500 text-xs sm:text-sm italic">
                  Sempre reagindo ao mundo — nunca o criando.
                </p>
              </div>
              
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed pl-8 sm:pl-9">
                Ideias vêm e vão, mas nada se concretiza. O foco some, a motivação evapora e a sensação de estagnação cresce.
              </p>
              
              <div className="bg-gray-800 border border-gray-700 p-4 sm:p-5 rounded-xl mt-4 sm:mt-6">
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <i className="ri-emotion-sad-line text-red-400 text-lg sm:text-xl"></i>
                  <span className="text-gray-300 font-bold text-sm sm:text-base" style={{ fontFamily }}>
                    O resultado?
                  </span>
                </div>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Frustração. Cansaço. E a sensação de estar sempre um passo atrás.
                </p>
              </div>
            </div>
          </div>

          {/* DEPOIS */}
          <div className="group relative bg-gradient-to-br from-yellow-900/30 via-yellow-800/20 to-black rounded-3xl p-6 sm:p-8 border-2 border-yellow-500/50 hover:border-yellow-400 transition-all duration-500 shadow-2xl shadow-yellow-500/20">
            <div className="absolute -top-4 sm:-top-5 left-6 sm:left-8">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-4 sm:px-6 py-2 rounded-full flex items-center gap-2 shadow-xl shadow-yellow-500/30">
                <div className="w-3 h-3 bg-yellow-200 rounded-full animate-pulse"></div>
                <span className="font-bold text-base sm:text-lg" style={{ fontFamily }}>
                  DEPOIS
                </span>
              </div>
            </div>
            
            <div className="mt-6 sm:mt-8 mb-4 sm:mb-6">
              <img 
                src="https://readdy.ai/api/search-image?query=golden%20glowing%20brain%20with%20brilliant%20neural%20networks%20and%20energy%20flow%2C%20mental%20clarity%20and%20enlightenment%20concept%2C%20bright%20golden%20light%20with%20clear%20pathways%20and%20power%2C%20luxury%20premium%20atmosphere&width=350&height=250&seq=enlightened-mobile&orientation=landscape"
                alt="Mente iluminada"
                className="w-full rounded-2xl shadow-2xl shadow-yellow-500/30 mb-4 sm:mb-6 border border-yellow-500/30 sm:hidden"
                loading="lazy"
                decoding="async"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=golden%20glowing%20brain%20with%20brilliant%20neural%20networks%20and%20energy%20flow%2C%20mental%20clarity%20and%20enlightenment%20concept%2C%20bright%20golden%20light%20with%20clear%20pathways%20and%20power%2C%20luxury%20premium%20atmosphere&width=500&height=350&seq=enlightened-premium&orientation=landscape"
                alt="Mente iluminada"
                className="w-full rounded-2xl shadow-2xl shadow-yellow-500/30 mb-4 sm:mb-6 border border-yellow-500/30 hidden sm:block"
                loading="lazy"
                decoding="async"
              />
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-4 sm:mb-6" style={{ fontFamily }}>
              A Mente Dourada
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-flashlight-line text-yellow-400 text-xl sm:text-2xl mt-1 flex-shrink-0"></i>
                <p className="text-yellow-300 text-sm sm:text-base font-semibold">Clareza. Energia. Propósito absoluto.</p>
              </div>
              
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed pl-8 sm:pl-9">
                Quando você decifra o Código, tudo muda. A mente deixa de ser um campo de ruído e se torna um <strong className="text-yellow-400">sistema de poder</strong>.
              </p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 sm:p-4 rounded-xl pl-8 sm:pl-9">
                <p className="text-yellow-400 text-xs sm:text-sm font-semibold">
                  Cada pensamento passa a ter direção. Cada ideia se transforma em execução.
                </p>
              </div>
              
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed pl-8 sm:pl-9">
                Você começa a pensar como quem já venceu, e o mundo simplesmente responde.
              </p>
              
              <p className="text-yellow-400 text-sm sm:text-base font-semibold pl-8 sm:pl-9">
                O foco volta. A energia se multiplica. E a realidade se alinha à sua visão.
              </p>
              
              <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border border-yellow-500/40 p-4 sm:p-5 rounded-xl mt-4 sm:mt-6">
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <i className="ri-lightbulb-flash-line text-yellow-400 text-lg sm:text-xl"></i>
                  <span className="text-yellow-400 font-bold text-sm sm:text-base" style={{ fontFamily }}>
                    Essa é a mente dourada.
                  </span>
                </div>
                <p className="text-gray-200 text-xs sm:text-sm">
                  Silenciosa, estratégica e magneticamente próspera.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA da transformação */}
        <div className="text-center px-4">
          <div className="inline-block bg-gradient-to-r from-yellow-500/10 via-yellow-600/10 to-yellow-500/10 border border-yellow-500/30 px-6 sm:px-8 py-4 sm:py-6 rounded-2xl backdrop-blur-sm max-w-4xl">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-2 sm:mb-3">
              <i className="ri-fire-line text-yellow-400 text-2xl sm:text-3xl"></i>
              <p className="text-yellow-400 font-bold text-lg sm:text-xl md:text-2xl text-center" style={{ fontFamily }}>
                Esta é a transformação que te aguarda
              </p>
              <i className="ri-fire-line text-yellow-400 text-2xl sm:text-3xl"></i>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm text-center">
              E ela começa no momento em que você decide desbloquear o código
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
